import {
  API_URL,
  put,
  request,
  takeEvery,
  debug,
  createDoRequestAction,
  assertTotalSchema,
  v,
  SagaIterator,
  call,
  CreateDoRequestActionOutput,
} from '../../lib'
import {clearDb} from '../db'
import {clearLogoutPending, waitForBootLogout} from '../../lib/auth/logout_pending'

import {
  doAUTH_SET,
  doPROFILE_SET,
  doSETTINGS_SET,
  doMFA_REQUIRED_SET,
  doREQUEST_ERROR,
  doREQUEST_COMPLETE,
  doPROJECT_SETTINGS_LIST_SET,
  doFAVORITE_LIST_SET,
  doIDENTITY_SET,
} from '../state'
import {doREQUEST_IDENTITY_UNREAD_ANNOUNCEMENT_LIST_FETCH} from './identity_unread_announcement_list_fetch'

export const REQUEST_IDENTITY_AUTHENTICATE = 'REQUEST_IDENTITY_AUTHENTICATE'
export const doREQUEST_IDENTITY_AUTHENTICATE =
  createDoRequestAction<IdentityAuthenticateRequestPayload>(REQUEST_IDENTITY_AUTHENTICATE)

export const requestIdentityAuthenticateActionCreators = {
  doREQUEST_IDENTITY_AUTHENTICATE,
}

export type IdentityAuthenticateRequestPayload = {
  email: string
  password: string
}

export function* requestIdentityAuthenticate(
  arg: CreateDoRequestActionOutput<IdentityAuthenticateRequestPayload>,
): SagaIterator {
  debug('saga*requestIdentityAuthenticate')
  const {payload, setRequestResult} = arg

  try {
    assertTotalSchema({
      payload,
      schema: v.object({
        requestId: v.string().uuid().exist(),
        email: v
          .string()
          .email({tlds: {allow: false}})
          .exist(),
        password: v.string().exist(),
      }),
    })
  } catch (error) {
    debug('saga*requestIdentityAuthenticate validation error', error)
    return yield put(
      doREQUEST_ERROR(
        {
          from: REQUEST_IDENTITY_AUTHENTICATE,
          requestId: payload.requestId,
          url: '/v1/web/identity-authenticate',
          error,
        },
        setRequestResult,
      ),
    )
  }

  const {email, password} = payload

  // a boot-time logout retry may still be in flight (this tab booted with the pending-logout
  // marker set). Its `Set-Cookie` clearing would otherwise land after the new login's cookie and
  // sign the user straight back out. Capped at 3s so a hung logout cannot hold the form hostage.
  yield call(waitForBootLogout)

  const result = yield request({
    method: 'post',
    url: `${API_URL}/v1/web/identity-authenticate`,
    data: {
      email,
      password,
    },
  })

  if (!result.success || result.error) {
    const error = result.error || new Error('request did not success')
    debug('saga*requestIdentityAuthenticate request error', error)

    return yield put(
      doREQUEST_ERROR(
        {
          fromType: REQUEST_IDENTITY_AUTHENTICATE,
          requestId: payload.requestId,
          url: '/v1/web/identity-authenticate',
          ...result,
        },
        setRequestResult,
      ),
    )
  }

  const {
    profile,
    settings,
    projectSettingsList,
    workspaceFavoriteList,
    accessToken,
    accessTokenExpiresInSeconds,
    refreshTokenExpiresInSeconds,
    identity,
  } = result.payload
  const {type} = result

  if (type == 'MFA_REQUIRED') {
    yield put(
      doREQUEST_COMPLETE(
        {
          fromType: REQUEST_IDENTITY_AUTHENTICATE,
          requestId: payload.requestId,
          ...result,
        },
        setRequestResult,
      ),
    )
    return yield put(
      doMFA_REQUIRED_SET({
        requestId: payload.requestId,
        ...result?.payload,
      }),
    )
  }

  yield call(clearDb)

  yield put(
    doAUTH_SET({
      accessToken,
      accessTokenExpiresInSeconds,
      refreshTokenExpiresInSeconds,
    }),
  )

  // a fresh session is the other way the marker retires -- whatever the old session's logout
  // never managed to say, this tab is signed in again and must not sign itself out on the next boot
  yield call(clearLogoutPending)

  yield put(doPROFILE_SET(profile || {}))
  yield put(doSETTINGS_SET(settings))
  yield put(doPROJECT_SETTINGS_LIST_SET(projectSettingsList))
  yield put(doFAVORITE_LIST_SET(workspaceFavoriteList))
  yield put(doIDENTITY_SET(identity))

  // Fetch unread announcements once after successful login
  // The accessToken is already available from the login response, so we can dispatch immediately
  debug('identity_authenticate: dispatching REQUEST_IDENTITY_UNREAD_ANNOUNCEMENT_LIST_FETCH')
  yield put(doREQUEST_IDENTITY_UNREAD_ANNOUNCEMENT_LIST_FETCH({payload: {}}))

  yield put(
    doREQUEST_COMPLETE(
      {
        fromType: REQUEST_IDENTITY_AUTHENTICATE,
        requestId: payload.requestId,
        ...result,
      },
      setRequestResult,
    ),
  )
}

export function* identityAuthenticateSaga() {
  yield takeEvery(REQUEST_IDENTITY_AUTHENTICATE, requestIdentityAuthenticate)
}
